export const cloneDeepObject = obj => {
  return JSON.parse(JSON.stringify(obj));
};

export const escapeHtml = str => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export function handleHtmlData(htmlData) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlData}</div>`, 'text/html');
  const container = doc.body.firstElementChild;

  const result = [];
  const assetTags = new Set(['LINK', 'STYLE', 'SCRIPT']);
  const generateId = () =>
    `section-freestyle-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  const generateAssetId = () =>
    `asset-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  let tempGroup = [];

  const flushTempGroup = () => {
    const content = tempGroup
      .map(node => node.outerHTML)
      .join('')
      .trim();
    if (!content) return;

    const id = generateId();
    const html = `
      <section
        class="container ck-content"
        data-section="${id}"
        data-type="section-freestyle"
        data-title="Freestyle"
      >
        <div
          data-attribute="wrap-content"
          data-top-padding="false"
          data-bottom-padding="false"
          data-layout-full-width="false"
          data-spacing-horizontal="true"
        >
          <div data-attribute="content">${content}</div>
        </div>
      </section>
    `;
    result.push({
      id,
      type: 'section-freestyle',
      title: 'Freestyle',
      html,
    });

    tempGroup = [];
  };

  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType !== 1 && node.nodeType !== 3) continue; //just handle element and text node

    const html = node.outerHTML?.trim() || node.textContent.trim();
    if (!html) continue;

    // Keep asset tags as-is (do not wrap into a freestyle section)
    if (node.nodeType === 1 && assetTags.has(node.tagName)) {
      flushTempGroup();
      const id = generateAssetId();
      result.push({
        id,
        type: 'asset',
        title: '',
        html: node.outerHTML,
        hidden: true,
      });
      continue;
    }

    if (node.nodeType === 1 && node.tagName === 'SECTION') {
      const el = node;
      let id = el.getAttribute('data-section');
      const type = el.getAttribute('data-type');
      let title = el.getAttribute('data-title');

      if (id && type) {
        flushTempGroup();

        if (!title) {
          title = '';
          el.setAttribute('data-title', title);
        }

        result.push({
          id,
          type,
          title,
          html: el.outerHTML,
        });
      } else {
        tempGroup.push(el);
      }
    } else {
      tempGroup.push(node);
    }
  }

  flushTempGroup();

  return result;
}

export const handleSectionTemplate = {
  insertNewId(template) {
    const isDev = process.env.NODE_ENV === 'development';
    if (!template || !template.html || !template.type) return {};
    const { image, html: htmlString, title, type: sectionType } = template;
    const newId = `${sectionType}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const prefix = 'prl-';
    const uniqueId = Math.random()
      .toString(36)
      .slice(2, 8);
    let replacedHtmlString = htmlString.replaceAll('unique-id', `${prefix}${uniqueId}`);
    if (!isDev) {
      replacedHtmlString = replacedHtmlString.replace(/https:\/\/cdn\.(skyfastimg|cacheimg)\.com\/+/g, '/uploads/');
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${replacedHtmlString}</div>`, 'text/html');
    const container = doc.body.firstElementChild;
    const el = container.querySelector('[data-section]');
    if (el) {
      el.setAttribute('data-section', newId);
      el.setAttribute('data-type', sectionType);
    }
    return {
      id: newId,
      title,
      type: sectionType,
      html: container.innerHTML.trim(),
      image,
    };
  },
};

export const rgbToHex = rgb => {
  if (!rgb) return null;
  // If already hex, return as is
  if (rgb.startsWith('#')) return rgb;

  // Extract rgb/rgba values
  const result = rgb.match(/\d+/g);
  if (!result) return null;

  const r = parseInt(result[0]);
  const g = parseInt(result[1]);
  const b = parseInt(result[2]);

  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
};

export const isYoutubeUrl = urlValue => {
  if (!urlValue) return false;
  try {
    const url = new URL(urlValue, window.location.origin);
    const hostname = url.hostname.replace(/^www\./, '').toLowerCase();
    return hostname === 'youtu.be' || hostname.endsWith('youtube.com') || hostname.endsWith('youtube-nocookie.com');
  } catch (_error) {
    return false;
  }
};

export const replaceImageUrl = html => {
  const isDev = process.env.NODE_ENV === 'development';
  return isDev ? html : html.replace(/https:\/\/cdn\.(skyfastimg|cacheimg)\.com\/+/g, '/uploads/');
};

export const parseYouTubeTimeToSeconds = value => {
  if (!value) return null;

  if (/^\d+$/.test(value)) return parseInt(value, 10);

  const match = value.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!match) return null;

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  const total = hours * 3600 + minutes * 60 + seconds;

  return total > 0 ? total : null;
};

export const normalizeYoutubeEmbedUrl = rawUrl => {
  if (!rawUrl) return '';

  try {
    const url = new URL(rawUrl, window.location.origin);
    const hostname = url.hostname.replace(/^www\./, '').toLowerCase();
    let videoId = '';

    if (hostname === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] || '';
    } else if (hostname.endsWith('youtube.com') || hostname.endsWith('youtube-nocookie.com')) {
      if (url.pathname.startsWith('/embed/')) {
        videoId = url.pathname.split('/')[2] || '';
      } else if (url.pathname.startsWith('/shorts/')) {
        videoId = url.pathname.split('/')[2] || '';
      } else {
        videoId = url.searchParams.get('v') || '';
      }
    }

    if (!videoId) return rawUrl;

    const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
    if (url.searchParams.get('autoplay') === '1') {
      embedUrl.searchParams.set('autoplay', '1');
    }
    const startValue = url.searchParams.get('start') || url.searchParams.get('t');
    const startSeconds = parseYouTubeTimeToSeconds(startValue);
    if (startSeconds) {
      embedUrl.searchParams.set('start', String(startSeconds));
    }

    return embedUrl.toString();
  } catch (_error) {
    return rawUrl;
  }
};

export const processFinalCatalogHtml = html => {
  // Swiper css
  const sliderCssHref = 'https://static.cdnstash.com/assets/css/section-slider.css';

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const container = doc.body.firstElementChild;

  // Remove existing slider css link
  const existingLinks = Array.from(container.querySelectorAll('link[data-slider="true"]')) || [];
  existingLinks.forEach(linkEl => {
    linkEl.remove();
  });

  const hasSwiperSection = Array.from(container.querySelectorAll('section[data-type]')).some(
    sectionEl => sectionEl.querySelector('.swiper') !== null,
  );

  if (hasSwiperSection) {
    const linkEl = doc.createElement('link');
    linkEl.setAttribute('href', sliderCssHref);
    linkEl.setAttribute('rel', 'stylesheet');
    linkEl.setAttribute('type', 'text/css');
    linkEl.setAttribute('media', 'all');
    linkEl.setAttribute('data-slider', 'true');
    container.insertBefore(linkEl, container.firstChild);
  }

  return container.innerHTML.trim();
};
